// Eine Berichts-Kachel mit allen vier Aktionen: Senden, Bearbeiten, Neu
// generieren, Löschen.
//
// Bewusst EINE Komponente für beide Orte (Monatsbericht-Seite und
// Analytics-Tab) — zwei Umsetzungen derselben Kachel würden garantiert
// auseinanderlaufen.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Pencil, RefreshCw, Send, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { deleteReport, generateReport, releaseReport, reviseReport } from '@/lib/analytics-api';
import PdfVorschau from '@/components/PdfVorschau';
import BerichtBearbeiten from '@/components/BerichtBearbeiten';

export interface BerichtKachelDaten {
  id: string;
  month: string;
  created_at: string;
  released_at: string | null;
  customer_id: string;
  kunde: string;
  report_email?: string | null;
  /** false = im Berichtsmonat wurde keine Optimierung umgesetzt; null/undefined = nicht anwendbar */
  optimierung_im_monat?: boolean | null;
}

function monatLabel(month: string): string {
  const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function BerichtKachel({
  b,
  zeigeKunde = true,
  invalidate,
}: {
  b: BerichtKachelDaten;
  /** Auf der Kundenseite steht der Name schon oben — dann hier weglassen. */
  zeigeKunde?: boolean;
  invalidate: () => void;
}) {
  const queryClient = useQueryClient();
  const monat = b.month.slice(0, 7);
  const [bearbeiten, setBearbeiten] = useState(false);
  // Nach einer Überarbeitung muss die Vorschau das NEUE PDF holen
  const [stand, setStand] = useState(0);

  const fertig = (nachricht: string) => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['alle-berichte'] });
    setStand((n) => n + 1);
    toast.success(nachricht);
  };

  const sendMut = useMutation({
    mutationFn: ({ benachrichtigen }: { benachrichtigen: boolean }) =>
      releaseReport(b.customer_id, monat, !b.released_at, benachrichtigen),
    onSuccess: (d) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['alle-berichte'] });
      if (b.released_at) return toast.success('Freigabe zurückgezogen');
      // Freigabe und Mail sind zwei Dinge — das muss sichtbar bleiben, sonst
      // hält man den Kunden für informiert, obwohl nichts rausging.
      if (d.mail?.gesendet) toast.success(`Gesendet — Mail an ${d.mail.an}`);
      else if (d.mail) toast(`Freigegeben, aber KEINE Mail: ${d.mail.grund}`, { icon: '⚠️', duration: 8000 });
      else toast.success('Freigegeben — im Kundenkonto sichtbar');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const neuMut = useMutation({
    mutationFn: () => generateReport(b.customer_id, monat),
    onSuccess: () => fertig('Neu generiert'),
    onError: (e: any) => toast.error(e.message),
  });

  const reviseMut = useMutation({
    mutationFn: (anweisung: string) => reviseReport(b.customer_id, monat, anweisung),
    onSuccess: () => { setBearbeiten(false); fertig('Bericht überarbeitet'); },
    onError: (e: any) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: () => deleteReport(b.customer_id, monat),
    onSuccess: () => fertig('Bericht gelöscht'),
    onError: (e: any) => toast.error(e.message),
  });

  const laeuft = sendMut.isPending || neuMut.isPending || reviseMut.isPending || delMut.isPending;
  const btn = 'inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium disabled:opacity-50';

  return (
    <div className="flex flex-col gap-2">
      <PdfVorschau
        key={stand}
        month={monat}
        customerId={b.customer_id}
        titel={`${b.kunde} — ${monatLabel(monat)}`}
        breite={190}
      />

      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">
          {zeigeKunde ? b.kunde : monatLabel(monat)}
        </p>
        <p className="text-[11px] text-gray-400">
          {zeigeKunde ? `${monatLabel(monat)} · ` : ''}
          erstellt {new Date(b.created_at).toLocaleDateString('de-DE')}
          {!b.report_email && ' · keine Berichts-E-Mail'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {b.released_at ? (
          <span className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
            <CheckCircle2 className="h-3 w-3" /> beim Kunden
          </span>
        ) : (
          <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
            Entwurf
          </span>
        )}
        {b.optimierung_im_monat === false && !b.released_at && (
          <span
            className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
            title={'Im Berichtsmonat wurde keine Empfehlung umgesetzt — das Kapitel „Das haben wir verbessert“ bleibt dünn. Runde läuft unter Analytics → Empfehlungen.'}
          >
            <AlertTriangle className="h-3 w-3" /> keine Optimierung
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => {
            if (b.released_at) {
              if (window.confirm(`Freigabe für ${b.kunde} zurückziehen? Der Kunde sieht den Bericht dann nicht mehr.`)) {
                sendMut.mutate({ benachrichtigen: false });
              }
              return;
            }
            // Sichtbares Signal VOR der Freigabe: Ohne umgesetzte Optimierung
            // im Berichtsmonat soll nichts unbemerkt rausgehen.
            const warnung =
              b.optimierung_im_monat === false
                ? '⚠️ ACHTUNG: Im Berichtsmonat wurde KEINE Optimierung umgesetzt — im Bericht fehlt das Kapitel über Verbesserungen.\n\n'
                : '';
            if (!window.confirm(`${warnung}Bericht ${monatLabel(monat)} für ${b.kunde} freigeben?\n\nDer Kunde sieht ihn dann in seinem Login.`)) return;
            const mailen = b.report_email
              ? window.confirm(
                  `Zusätzlich eine kurze E-Mail an ${b.report_email} schicken?\n\nOK = freigeben UND Mail\nAbbrechen = nur freigeben`
                )
              : false;
            sendMut.mutate({ benachrichtigen: mailen });
          }}
          disabled={laeuft}
          className={`${btn} ${b.released_at ? 'border border-green-300 text-green-700 hover:bg-green-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          {sendMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {b.released_at ? 'Zurückziehen' : 'Senden'}
        </button>

        <button
          onClick={() => setBearbeiten(true)}
          disabled={laeuft}
          className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
        >
          <Pencil className="h-3 w-3" /> Bearbeiten
        </button>

        <button
          onClick={() => {
            if (
              window.confirm(
                `Bericht für ${monatLabel(monat)} neu generieren?\n\nDer bisherige Text wird ersetzt. Änderungen aus „Bearbeiten" gehen dabei verloren.`
              )
            ) neuMut.mutate();
          }}
          disabled={laeuft}
          className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
          title="Aus den aktuellen Daten komplett neu schreiben"
        >
          {neuMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Neu generieren
        </button>

        <button
          onClick={() => {
            const warnung = b.released_at
              ? `Bericht für ${monatLabel(monat)} LÖSCHEN?\n\nEr ist beim Kunden freigegeben — nach dem Löschen ist er dort weg.`
              : `Bericht für ${monatLabel(monat)} löschen?`;
            if (window.confirm(warnung)) delMut.mutate();
          }}
          disabled={laeuft}
          className={`${btn} border border-red-200 text-red-600 hover:bg-red-50`}
        >
          {delMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          Löschen
        </button>
      </div>

      {bearbeiten && (
        <BerichtBearbeiten
          kunde={b.kunde}
          monat={monatLabel(monat)}
          laeuft={reviseMut.isPending}
          onAbbrechen={() => setBearbeiten(false)}
          onSenden={(anweisung) => reviseMut.mutate(anweisung)}
        />
      )}
    </div>
  );
}
